import { computed, ref } from 'vue'

import { defineStore, acceptHMRUpdate } from 'pinia'

import { useAuthStore } from './auth'
import { useNoteFeature } from './Caregiver/feature'

import { useNProgress } from '@vueuse/integrations/useNProgress'


/**
 * @typedef {Object} CaregiverSearchParams
 * @property {string} [status]
 * @property {string[]} [matchingCriterias]
 * @property {string} [living_preference]
 * @property {Object} [destination]
 * @property {string} [destination.zip]
 * @property {number} [max_travel_time]
 * @property {boolean} [flexible_fill_in]
 * @property {boolean} [alternate_weekends_only]
 * @property {boolean} [exact_match]
 * @property {boolean} [my_candidate]
 * @property {number} [limit]
 * @property {number} [offset]
 * @property {string} [sort_by]
 */

export const useCaregiverSearchStore = defineStore('caregiverSearch', () => {

    const authStore = useAuthStore()

    const { isLoading } = useNProgress()

    // ── Default search state shape ──────────────────────────────────────

    const defaultSearchState = {
        living_preference: '',
        max_travel_time: null,
        address: '',
        city: '',
        state: null,
        in_state_only: false,
        zip: null,
        status: 'active',
        all_candidates: true,
        sort_order: { value: 'smart' },
        criterias: [],
        service_types: ['elder_care']
    }

    // ── Search form state ───────────────────────────────────────────────

    const searchState = ref({ ...defaultSearchState })

    /** Snapshot of searchState captured on search execution, used for sync comparison. */
    const lastSearchedState = ref(null)

    /** Transformed API payload from the last successful search (consumed by CaregiverSearchResults). */
    const currentSearchPayload = ref(null)

    // ── Search results ──────────────────────────────────────────────────

    // ── Search results ──────────────────────────────────────────────────

    const caregivers = ref([])
    const totalCount = ref(0)
    const preferredSortOrder = ref('smart')

    // ── Execution state ─────────────────────────────────────────────────

    /** Whether a search API request is currently in-flight. */
    const isRequestPending = ref(false)
    /** Whether a search session is active (user has executed at least one search). */
    const isSearching = ref(false)
    const noMoreResult = ref(false)
    const hasLoadedSecondPage = ref(false)
    const limitOffset = ref({ limit: 25, offset: 0 })

    // ── Computed helpers ────────────────────────────────────────────────

    const searchMustHaves = computed(() => {
        return (searchState.value.criterias || []).filter(c => c.factor === 'must_have')
    })

    const searchWowFactors = computed(() => {
        return (searchState.value.criterias || []).filter(c => c.factor === 'wow')
    })

    const hasDefaultSearchState = computed(() => {
        const ignoredKeys = ['sort_order', 'client_id', 'living_preference']
        const currentState = { ...searchState.value }
        const defaultState = { ...defaultSearchState }
        ignoredKeys.forEach(key => {
            delete currentState[key]
            delete defaultState[key]
        })
        return Object.keys(defaultState).every(key =>
            JSON.stringify(currentState[key]) === JSON.stringify(defaultState[key])
        )
    })

    // ── Sync detection ──────────────────────────────────────────────────

    function buildSearchFingerprint(state) {
        return JSON.stringify({
            living_preference: state.living_preference || '',
            max_travel_time: state.max_travel_time || null,
            address: state.address || '',
            city: state.city || '',
            state: state.state || null,
            in_state_only: !!state.in_state_only,
            zip: state.zip || null,
            status: state.status || 'active',
            all_candidates: state.all_candidates ?? true,
            client_id: state.client_id || null,
            criterias: (state.criterias || []).map(c => ({
                name_id: c.name_id,
                factor: c.factor,
                attributes: (c.attributes || []).map(a => ({
                    name_id: a.name_id,
                    selected_choice_ids: [...(a.selected_choice_ids || [])].sort()
                })).sort((a, b) => String(a.name_id).localeCompare(String(b.name_id)))
            })).sort((a, b) => String(a.name_id).localeCompare(String(b.name_id))),
            availability: (state.availability || []).map(s => ({
                day: s.day,
                start_hour: s.start_hour,
                end_hour: s.end_hour
            })).sort((a, b) => a.day.localeCompare(b.day)),
            alternate_weekends_only: !!state.alternate_weekends_only,
            exact_match: !!state.exact_match,
            flexible_fill_in: !!state.flexible_fill_in,
            service_types: [...(state.service_types || [])].sort(),
        });
    }

    const searchParamsChanged = computed(() => {
        if (!lastSearchedState.value) return false
        return buildSearchFingerprint(searchState.value) !== buildSearchFingerprint(lastSearchedState.value);
    })

    const sortOrderChanged = computed(() => {
        if (!lastSearchedState.value) return false
        const current = searchState.value.sort_order?.value || searchState.value.sort_order || 'smart'
        const last = lastSearchedState.value.sort_order?.value || lastSearchedState.value.sort_order || 'smart'
        return current !== last;
    })

    const hasUnsyncedChanges = computed(() => searchParamsChanged.value || sortOrderChanged.value)

    // ── Payload helpers ─────────────────────────────────────────────────

    function transformCriteriasToPayloadFormat(criterias) {
        if (!criterias || criterias.length === 0) return {}
        return criterias.reduce((acc, criteria) => {
            if (!criteria.attributes || criteria.attributes.length === 0) {
                acc[criteria.name_id] = null
            } else {
                acc[criteria.name_id] = criteria.attributes.reduce((attrAcc, attr) => {
                    attrAcc[attr.name_id] = attr.selected_choice_ids || []
                    return attrAcc
                }, {})
            }
            return acc;
        }, {})
    }

    // ── State management ────────────────────────────────────────────────

    function snapshotSearchState() {
        lastSearchedState.value = JSON.parse(JSON.stringify(searchState.value))
    }

    function resetPagination() {
        limitOffset.value.offset = 0
        noMoreResult.value = false
    }

    function resetSearchState() {
        searchState.value = { ...defaultSearchState, criterias: [] }
        lastSearchedState.value = null
        currentSearchPayload.value = null
        caregivers.value = []
        totalCount.value = 0
        limitOffset.value.offset = 0
        noMoreResult.value = false
        hasLoadedSecondPage.value = false
        isRequestPending.value = false
        isSearching.value = false
    }

    // ── API ─────────────────────────────────────────────────────────────

    let abortController = null
    let currentRequestId = 0

    async function search(searchParams = {}) {
        // Cancel previous request if still pending
        if (abortController) {
            abortController.abort()
            isLoading.value = false
        }
        abortController = new AbortController()
        const requestId = ++currentRequestId

        isRequestPending.value = true
        isSearching.value = true
        isLoading.value = true

        return await new Promise((resolve, reject) => {
            axios.post(
                `/api/v1/search/caregivers`,
                searchParams,
                { signal: abortController.signal }
            ).then(response => {
                if (requestId !== currentRequestId) return;
                // Response format: { data: [...], meta: { total: X } }
                const caregiverData = response.data.data || []
                const total = response.data.meta?.total ?? caregiverData.length

                totalCount.value = total
                if (searchParams.offset === 0) {
                    caregivers.value = caregiverData
                } else {
                    caregivers.value = [...caregivers.value, ...caregiverData]
                }
                isRequestPending.value = false
                isLoading.value = false
                resolve(caregiverData)
            }).catch((err) => {
                if (requestId !== currentRequestId) return;
                isRequestPending.value = false
                isLoading.value = false
                if (axios.isCancel(err)) {
                    reject({ "error": err })
                    return
                }
                authStore.handleError(err)
                reject({ "error": err })
            })
        });
    }

    function selectCaregiver(caregiver_id) {
        if (caregiver_id === null) {
            return null;
        }

        const caregiver = caregivers.value.find(caregiver => caregiver.id === caregiver_id)
        return { ...useNoteFeature(caregiver, authStore) };
    }

    return {
        defaultSearchState,
        searchState,
        lastSearchedState,
        currentSearchPayload,
        caregivers,
        totalCount,
        preferredSortOrder,
        isRequestPending,
        isSearching,
        noMoreResult,
        hasLoadedSecondPage,
        limitOffset,
        searchMustHaves,
        searchWowFactors,
        hasDefaultSearchState,
        searchParamsChanged,
        sortOrderChanged,
        hasUnsyncedChanges,
        transformCriteriasToPayloadFormat,
        snapshotSearchState,
        resetPagination,
        resetSearchState,
        search,
        selectCaregiver
    };
})

if (import.meta.hot) {
    import.meta.hot.accept(acceptHMRUpdate(useCaregiverSearchStore, import.meta.hot))
}
